import { createGlobalStyle } from 'styled-components'

/**
 * Styles for rich-text content authored with the Quill editor in the
 * admin/instructor portals and rendered here via innerHTML
 * (WebContentUnit, AssignmentUnit, @eduact/ed-components Test questions,
 * TestSummary, admission MCQs).
 *
 * Ported from quill/dist/quill.core.css v2.0.2 with the `.ql-editor`
 * scope removed, because rendered content is not wrapped in that class
 * (and @eduact/ed-components markup cannot be wrapped). Selectors are
 * kept safe for the rest of the app by relying on ql-* class names and
 * the Quill-specific li[data-list] attribute.
 *
 * Newly authored content carries alignment/direction as inline styles and
 * does not depend on this file; the class-based rules below keep legacy
 * content (ql-align-*, ql-indent-*, ql-direction-rtl) rendering correctly.
 */
const QuillContentStyles = createGlobalStyle`
  /* ---- Lists (Quill 2 emits <ol><li data-list="..."><span class="ql-ui"/>...) ---- */
  ol:has(> li[data-list]) {
    padding-left: 1.5em;
    counter-reset: list-0 list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
    margin: 0;
  }
  li[data-list] {
    list-style-type: none;
    padding-left: 1.5em;
    position: relative;
  }
  .ql-ui {
    position: absolute;
  }
  li[data-list] > .ql-ui:before {
    display: inline-block;
    margin-left: -1.5em;
    margin-right: 0.3em;
    text-align: right;
    white-space: nowrap;
    width: 1.2em;
  }
  li[data-list='checked'] > .ql-ui,
  li[data-list='unchecked'] > .ql-ui {
    color: #777;
  }
  li[data-list='bullet'] > .ql-ui:before {
    content: '\\2022';
  }
  li[data-list='checked'] > .ql-ui:before {
    content: '\\2611';
  }
  li[data-list='unchecked'] > .ql-ui:before {
    content: '\\2610';
  }
  li[data-list] {
    counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
  }
  li[data-list='ordered'] {
    counter-increment: list-0;
  }
  li[data-list='ordered'] > .ql-ui:before {
    content: counter(list-0, decimal) '. ';
  }
  li[data-list='ordered'].ql-indent-1 {
    counter-increment: list-1;
  }
  li[data-list='ordered'].ql-indent-1 > .ql-ui:before {
    content: counter(list-1, lower-alpha) '. ';
  }
  li[data-list].ql-indent-1 {
    counter-reset: list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
  }
  li[data-list='ordered'].ql-indent-2 {
    counter-increment: list-2;
  }
  li[data-list='ordered'].ql-indent-2 > .ql-ui:before {
    content: counter(list-2, lower-roman) '. ';
  }
  li[data-list].ql-indent-2 {
    counter-reset: list-3 list-4 list-5 list-6 list-7 list-8 list-9;
  }
  li[data-list='ordered'].ql-indent-3 {
    counter-increment: list-3;
  }
  li[data-list='ordered'].ql-indent-3 > .ql-ui:before {
    content: counter(list-3, decimal) '. ';
  }
  li[data-list].ql-indent-3 {
    counter-reset: list-4 list-5 list-6 list-7 list-8 list-9;
  }
  li[data-list='ordered'].ql-indent-4 {
    counter-increment: list-4;
  }
  li[data-list='ordered'].ql-indent-4 > .ql-ui:before {
    content: counter(list-4, lower-alpha) '. ';
  }
  li[data-list].ql-indent-4 {
    counter-reset: list-5 list-6 list-7 list-8 list-9;
  }
  li[data-list='ordered'].ql-indent-5 {
    counter-increment: list-5;
  }
  li[data-list='ordered'].ql-indent-5 > .ql-ui:before {
    content: counter(list-5, lower-roman) '. ';
  }
  li[data-list].ql-indent-5 {
    counter-reset: list-6 list-7 list-8 list-9;
  }
  li[data-list='ordered'].ql-indent-6 {
    counter-increment: list-6;
  }
  li[data-list='ordered'].ql-indent-6 > .ql-ui:before {
    content: counter(list-6, decimal) '. ';
  }
  li[data-list].ql-indent-6 {
    counter-reset: list-7 list-8 list-9;
  }
  li[data-list='ordered'].ql-indent-7 {
    counter-increment: list-7;
  }
  li[data-list='ordered'].ql-indent-7 > .ql-ui:before {
    content: counter(list-7, lower-alpha) '. ';
  }
  li[data-list].ql-indent-7 {
    counter-reset: list-8 list-9;
  }
  li[data-list='ordered'].ql-indent-8 {
    counter-increment: list-8;
  }
  li[data-list='ordered'].ql-indent-8 > .ql-ui:before {
    content: counter(list-8, lower-roman) '. ';
  }
  li[data-list].ql-indent-8 {
    counter-reset: list-9;
  }
  li[data-list='ordered'].ql-indent-9 {
    counter-increment: list-9;
  }
  li[data-list='ordered'].ql-indent-9 > .ql-ui:before {
    content: counter(list-9, decimal) '. ';
  }

  /* ---- Indentation (class-based; used by both legacy and current content) ---- */
  .ql-indent-1:not(.ql-direction-rtl) { padding-left: 3em; }
  li.ql-indent-1:not(.ql-direction-rtl) { padding-left: 4.5em; }
  .ql-indent-1.ql-direction-rtl.ql-align-right { padding-right: 3em; }
  li.ql-indent-1.ql-direction-rtl.ql-align-right { padding-right: 4.5em; }
  .ql-indent-2:not(.ql-direction-rtl) { padding-left: 6em; }
  li.ql-indent-2:not(.ql-direction-rtl) { padding-left: 7.5em; }
  .ql-indent-2.ql-direction-rtl.ql-align-right { padding-right: 6em; }
  li.ql-indent-2.ql-direction-rtl.ql-align-right { padding-right: 7.5em; }
  .ql-indent-3:not(.ql-direction-rtl) { padding-left: 9em; }
  li.ql-indent-3:not(.ql-direction-rtl) { padding-left: 10.5em; }
  .ql-indent-3.ql-direction-rtl.ql-align-right { padding-right: 9em; }
  li.ql-indent-3.ql-direction-rtl.ql-align-right { padding-right: 10.5em; }
  .ql-indent-4:not(.ql-direction-rtl) { padding-left: 12em; }
  li.ql-indent-4:not(.ql-direction-rtl) { padding-left: 13.5em; }
  .ql-indent-4.ql-direction-rtl.ql-align-right { padding-right: 12em; }
  li.ql-indent-4.ql-direction-rtl.ql-align-right { padding-right: 13.5em; }
  .ql-indent-5:not(.ql-direction-rtl) { padding-left: 15em; }
  li.ql-indent-5:not(.ql-direction-rtl) { padding-left: 16.5em; }
  .ql-indent-5.ql-direction-rtl.ql-align-right { padding-right: 15em; }
  li.ql-indent-5.ql-direction-rtl.ql-align-right { padding-right: 16.5em; }
  .ql-indent-6:not(.ql-direction-rtl) { padding-left: 18em; }
  li.ql-indent-6:not(.ql-direction-rtl) { padding-left: 19.5em; }
  .ql-indent-6.ql-direction-rtl.ql-align-right { padding-right: 18em; }
  li.ql-indent-6.ql-direction-rtl.ql-align-right { padding-right: 19.5em; }
  .ql-indent-7:not(.ql-direction-rtl) { padding-left: 21em; }
  li.ql-indent-7:not(.ql-direction-rtl) { padding-left: 22.5em; }
  .ql-indent-7.ql-direction-rtl.ql-align-right { padding-right: 21em; }
  li.ql-indent-7.ql-direction-rtl.ql-align-right { padding-right: 22.5em; }
  .ql-indent-8:not(.ql-direction-rtl) { padding-left: 24em; }
  li.ql-indent-8:not(.ql-direction-rtl) { padding-left: 25.5em; }
  .ql-indent-8.ql-direction-rtl.ql-align-right { padding-right: 24em; }
  li.ql-indent-8.ql-direction-rtl.ql-align-right { padding-right: 25.5em; }
  .ql-indent-9:not(.ql-direction-rtl) { padding-left: 27em; }
  li.ql-indent-9:not(.ql-direction-rtl) { padding-left: 28.5em; }
  .ql-indent-9.ql-direction-rtl.ql-align-right { padding-right: 27em; }
  li.ql-indent-9.ql-direction-rtl.ql-align-right { padding-right: 28.5em; }

  /* ---- Direction & alignment (legacy class-based content) ---- */
  li.ql-direction-rtl { padding-right: 1.5em; }
  li.ql-direction-rtl > .ql-ui:before {
    margin-left: 0.3em;
    margin-right: -1.5em;
    text-align: left;
  }
  .ql-direction-rtl {
    direction: rtl;
    text-align: inherit;
  }
  .ql-align-center { text-align: center; }
  .ql-align-justify { text-align: justify; }
  .ql-align-right { text-align: right; }

  /* ---- Inline classes (size / font / colors) ---- */
  .ql-size-small { font-size: 0.75em; }
  .ql-size-large { font-size: 1.5em; }
  .ql-size-huge { font-size: 2.5em; }
  .ql-font-serif { font-family: Georgia, 'Times New Roman', serif; }
  .ql-font-monospace { font-family: Monaco, 'Courier New', monospace; }
  .ql-bg-black { background-color: #000; }
  .ql-bg-red { background-color: #e60000; }
  .ql-bg-orange { background-color: #f90; }
  .ql-bg-yellow { background-color: #ff0; }
  .ql-bg-green { background-color: #008a00; }
  .ql-bg-blue { background-color: #06c; }
  .ql-bg-purple { background-color: #93f; }
  .ql-color-white { color: #fff; }
  .ql-color-red { color: #e60000; }
  .ql-color-orange { color: #f90; }
  .ql-color-yellow { color: #ff0; }
  .ql-color-green { color: #008a00; }
  .ql-color-blue { color: #06c; }
  .ql-color-purple { color: #93f; }

  /* ---- Blocks ---- */
  .ql-code-block-container {
    font-family: monospace;
    white-space: pre-wrap;
  }
  .ql-video {
    display: block;
    max-width: 100%;
  }
  .ql-video.ql-align-center { margin: 0 auto; }
  .ql-video.ql-align-right { margin: 0 0 0 auto; }
`

export default QuillContentStyles
